import { useEffect, useMemo } from 'react';

import {
  PROFILE_MENU_NAV,
  resolveNav,
} from '@personal-trainer-app/shared/src/config/nav.config';
import {
  describeInvitationsMeta,
  invitationsLeft,
} from '@personal-trainer-app/shared/src/utils/invitation/invitation-quota';

import { UserDropdownItem } from './UserDropdownItem';

import type { UserDropdownMenuProps } from './UserDropdown.types';
import type {
  NavEntry,
  NavItem,
} from '@personal-trainer-app/shared/src/config/nav.config';

import { Avatar } from '@/components/ui/Avatar';
import { PersonIdentity } from '@/components/ui/PersonIdentity';
import { useFocusTrap, useKeyboardNavigation } from '@/hooks';
import { navIcon } from '@/layouts/components/navIcons';

/** The row that carries what is left of the invitation quota. */
const INVITE = 'invite';
const SIGN_OUT = 'sign-out';

/**
 * Split the resolved entries into groups at each divider.
 *
 * The manifest is a flat list because that is the shape both platforms read;
 * web draws the groups as ruled sections, so it regroups on the way in. Doing
 * it from the resolved list rather than the raw one means a section whose only
 * item was gated away never renders a rule over nothing.
 */
function toGroups(entries: NavEntry[]): NavItem[][] {
  const groups: NavItem[][] = [[]];

  for (const entry of entries) {
    if (entry.kind === 'divider') {
      groups.push([]);
      continue;
    }
    groups[groups.length - 1]!.push(entry);
  }

  return groups.filter((group) => group.length > 0);
}

/**
 * The dropdown/bottom sheet menu content for the user dropdown.
 *
 * The items are the shared nav manifest's — order, labels, glyphs, grouping and
 * role gates all come from `nav.config.ts`, so this menu and the mobile user
 * sheet cannot drift apart the way they did. The icons were eight hand-copied
 * heroicons v1 paths until then, which had quietly diverged both from the
 * installed package and from the v2 glyphs mobile draws for the same rows.
 */
export function UserDropdownMenu({
  user,
  role,
  isOnline = false,
  isOpen,
  onClose,
  onSelect,
  signOutLabel = null,
  isMobile = false,
  triggerRef,
}: UserDropdownMenuProps) {
  // Focus trap - traps focus inside menu when open, returns focus to trigger on close
  const { containerRef: menuRef } = useFocusTrap({
    isActive: isOpen,
    returnFocusRef: triggerRef,
    autoFocus: true,
  });

  // Keyboard navigation - Arrow Up/Down, Home/End, Enter/Space for menu items
  useKeyboardNavigation({
    isActive: isOpen,
    containerRef: menuRef,
    onEscape: onClose,
  });

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Small delay to prevent immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, menuRef]);

  const inviteMeta = describeInvitationsMeta(invitationsLeft(user));

  const groups = useMemo(
    () => toGroups(resolveNav(PROFILE_MENU_NAV, { role, platform: 'web' })),
    [role]
  );

  const menuStyles = isMobile
    ? `
        fixed top-auto left-4 right-4 w-auto z-[100]
        bottom-[calc(theme(spacing.16)+env(safe-area-inset-bottom)+8px)]
        origin-bottom
        ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-2'}
      `
    : `
        absolute top-[calc(100%+8px)] right-0 w-60 z-[100]
        origin-top-right
        ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-2'}
      `;

  // Border radius: mobile uses flat bottom (anchored to screen edge), desktop uses full rounding
  const borderRadiusStyles = isMobile
    ? 'rounded-t-xl rounded-b-none'
    : 'rounded-xl';

  return (
    <div
      ref={menuRef}
      className={`
        bg-contrast-950 border border-stroke-default ${borderRadiusStyles}
        shadow-lift-popover
        overflow-hidden
        transition-all duration-150 ease-out
        ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
        ${menuStyles}
      `}
      role="menu"
      aria-hidden={!isOpen}
    >
      {/* Header with user info */}
      <div className="p-4 border-b border-stroke-subtle flex items-center gap-3">
        {/* The accent marks this as you, matching the trigger. */}
        <Avatar
          src={user.avatar}
          name={user.username}
          size="md"
          tone="accent"
          data-testid="user-dropdown-avatar"
        />
        <PersonIdentity
          person={{ name: null, username: user.username }}
          meta={
            <span className="flex items-center gap-1">
              {/* Presence, not a notification: not a NotificationDot. */}
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isOnline ? 'bg-success-400' : 'bg-neutral-600'
                }`}
              />
              {isOnline ? 'Online' : 'Offline'}
            </span>
          }
        />
      </div>

      {groups.map((group, index) => (
        <div
          key={group[0]!.id}
          className={`py-2 ${index > 0 ? 'border-t border-stroke-subtle' : ''}`}
        >
          {group.map((item) => {
            const leaves = item.id === SIGN_OUT && signOutLabel !== null;
            return (
              <UserDropdownItem
                key={item.id}
                icon={navIcon(item.icon, 'h-full w-full')}
                label={leaves ? signOutLabel : item.label}
                onClick={() => onSelect(item)}
                {...(item.danger && !leaves ? { isDanger: true } : {})}
                {...(item.id === INVITE ? { meta: inviteMeta } : {})}
                // The hint is a keyboard's, so it goes where there is one.
                {...(item.shortcut && !isMobile
                  ? { shortcut: item.shortcut }
                  : {})}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default UserDropdownMenu;
